import { useState } from 'react';
import type { FundInput, FundPeriod } from '@tahi/core';
import { Button, Chip } from '../../../ui';
import { Segmented } from '../../../ui/Segmented';
import { TextField } from '../../../ui/TextField';
import { copy } from '../copy';
import { minorToInput, parsePesoInput, periodLabel } from '../format';
import { LABEL } from '../styles';

const PERIODS: FundPeriod[] = ['weekly', 'per_cutoff', 'monthly'];

export interface FundFormProps {
  initial?: FundInput;
  /** Live salary sources, for "Refills on paydays of" (shown when there is more than one). */
  salarySources: { id: string; name: string }[];
  /** v0.5: money accounts, for "Kept in" (the first picked is the limit's default "From"). */
  accounts?: { id: string; name: string }[];
  error?: string | null;
  onSubmit(input: FundInput): void | Promise<void>;
}

/**
 * A spending limit (v0.5 copy; internally a fund). The name is required and the form says so
 * instead of silently disabling Save (Ivan, 2026-11-03: limits must always have a name).
 */
export function FundForm({
  initial,
  salarySources,
  accounts,
  error = null,
  onSubmit,
}: FundFormProps) {
  const [name, setName] = useState(initial?.name ?? '');
  const [amount, setAmount] = useState(initial ? minorToInput(initial.amountMinor) : '');
  const [period, setPeriod] = useState<FundPeriod>(initial?.period ?? 'per_cutoff');
  const [refill, setRefill] = useState<string | null>(initial?.refillFromIncomeSourceId ?? null);
  const [keptIn, setKeptIn] = useState<string[]>(initial?.accountIds ?? []);
  const [nameError, setNameError] = useState<string | null>(null);
  const [amountError, setAmountError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const minor = parsePesoInput(amount);

  async function submit() {
    const missingName = name.trim() === '';
    setNameError(missingName ? copy.fundRequired : null);
    setAmountError(minor === null ? copy.enterAmount : null);
    if (missingName || minor === null) return;
    setBusy(true);
    try {
      await onSubmit({
        name: name.trim(),
        amountMinor: minor,
        period,
        refillFromIncomeSourceId: period === 'per_cutoff' ? refill : null,
        ...(accounts ? { accountIds: keptIn } : {}),
      });
    } finally {
      setBusy(false);
    }
  }

  const toggle = (id: string) =>
    setKeptIn((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));

  return (
    <div className="flex flex-col gap-4">
      <TextField
        label={copy.fundName}
        value={name}
        maxLength={40}
        required
        error={nameError}
        onChange={(e) => {
          setName(e.target.value);
          if (e.target.value.trim() !== '') setNameError(null);
        }}
      />
      <TextField
        label={copy.fundAmount}
        inputMode="decimal"
        value={amount}
        error={amountError}
        onChange={(e) => setAmount(e.target.value)}
      />
      <Segmented<FundPeriod>
        label={copy.fundPeriod}
        value={period}
        options={PERIODS.map((p) => ({ value: p, label: periodLabel(p) }))}
        onChange={setPeriod}
      />
      {period === 'per_cutoff' && salarySources.length > 1 ? (
        <div role="group" aria-label={copy.refillFrom} className="flex flex-wrap gap-2">
          <Chip
            selected={refill === null}
            aria-pressed={refill === null}
            onClick={() => setRefill(null)}
          >
            {copy.firstSalary}
          </Chip>
          {salarySources.map((s) => (
            <Chip
              key={s.id}
              selected={refill === s.id}
              aria-pressed={refill === s.id}
              onClick={() => setRefill(s.id)}
            >
              {s.name}
            </Chip>
          ))}
        </div>
      ) : null}
      {accounts && accounts.length > 0 ? (
        <div className="flex flex-col gap-2">
          <span aria-hidden="true" className={LABEL}>
            {copy.keptIn}
          </span>
          <div role="group" aria-label={copy.keptIn} className="flex flex-wrap gap-2">
            {accounts.map((a) => {
              const i = keptIn.indexOf(a.id);
              return (
                <Chip
                  key={a.id}
                  selected={i >= 0}
                  aria-pressed={i >= 0}
                  onClick={() => toggle(a.id)}
                >
                  {a.name}
                  {i === 0 ? ` · ${copy.defaultBadge.toLowerCase()}` : ''}
                </Chip>
              );
            })}
          </div>
          <p className="text-sm text-muted">{copy.keptInHint}</p>
        </div>
      ) : null}
      {error ? (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      ) : null}
      <Button variant="primary" block disabled={busy} onClick={() => void submit()}>
        {copy.save}
      </Button>
    </div>
  );
}
